import superagent from 'superagent';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {getFuzzyLocalTimeFromPoint} from '@mapbox/timespace';
import {getPosixStringForCurrentYear} from '../util/posixTime';

export const LocationState = {
	notSearched: 'notSearched',
	valid: 'valid',
	notFound: 'notFound',
	outOfRange: 'outOfRange'
};
export const fetchPvgisHourly = createAsyncThunk(
	'solar/fetchPVGIS',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pvgisUrl}/seriescalc`)
			.query(options)
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchSolarLocation = createAsyncThunk(
	'solar/fetchSolarLocation',
	(options, {getState}) =>
		superagent
			.get('https://maps.googleapis.com/maps/api/geocode/json?address=' + options.address + '&key=AIzaSyAulSCwO2HXl5YuTFc_Qy43XMT-Yc2KRao')
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchSolarGeolocation = createAsyncThunk(
	'solar/fetchSolarGeolocation',
	(options, {getState}) =>
		superagent
			// TODO, get the location from Google api
			.get('https://maps.googleapis.com/maps/api/geocode/json?latlng=' + options.latlng + '&key=AIzaSyAulSCwO2HXl5YuTFc_Qy43XMT-Yc2KRao')
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error)
);

export const initialSelfSufficiency = {
	AetoPro: {
		summer: 3,
		spring: 5,
		winter: 7
	},
	AetoBasic: {
		summer: 10,
		spring: 10,
		winter: 10
	}
};

export const initialOrientation = {
	front: {
		pvgisDaily: null,
		pvgisMonthly: null,
	},
	left: {
		pvgisDaily: null,
		pvgisMonthly: null,
	},
	right: {
		pvgisDaily: null,
		pvgisMonthly: null,
	},
	back: {
		pvgisDaily: null,
		pvgisMonthly: null,
	},
};

export const initialCalculated = {
	summer: {
		power: 0,
		lum: 0,
	},
	spring: {
		power: 0,
		lum: 0,
	},
	winter: {
		power: 0,
		lum: 0,
	}
};

export const initialSolarParameters = {
	AetoPro: {
		maxBatteryCharge: {
			winter: 0.85,
			spring: 0.85,
			summer: 0.85,
		},
		startSafety: 0.25,
		powerLimit: 33,
		deepDischargeLimit: 0.25,
		selfConsumption: {
			1: 1,
			2: 1.5
		},
		dimmSafety: 0.25
	},
	AetoBasic: {
		maxBatteryCharge: {
			winter: 0.85,
			spring: 0.85,
			summer: 0.85,
		},
		startSafety: 0.55,
		powerLimit: 33,
		deepDischargeLimit: 0.25,
		selfConsumption: {
			1: 0.5,
			2: 0.5
		},
		dimmSafety: 0.25
	}
};

const solarInitialState = {
	status: 'idle',
	data: {
		project: {
			location: null,
			orientationOffset: 0,
			panelPdc: 28,
			useCase: null,
			luminarCount: 'one',
			luminarPower: 22, // power usage of the luminar in W
			shadowLevel: 'none',
			activityLevel: 'none', // none, low, medium, high
			batteryType: 'NiMH', // NiMH, SiB
			batteryReserve: 0.2, // amount of daily input to use
			batteryVoltage: 12, // voltage of Battery
			electricLoss: 0.9, // electric loss in the system
			pvReserve: 0.5, // maximum amount of solar input to use
			version: 'pro', // basic, pro
			temperature: '2200', // 2200, 2700, 3000, 4000
			dimmProfile: 'profile0', // nature, mixzonePark, f8
			lumPosition1: '1',
			lumPosition2: '1',
			projectName: '',
			selfSufficiency: initialSelfSufficiency,
			parameters: initialSolarParameters,
			azimuth: 'n',
			orientation: initialOrientation,
			calculated: initialCalculated,
		},
		locationState: LocationState.valid,
	},
	error: {},
};
export const solarSlice = createSlice({
	name: 'solar',
	initialState: solarInitialState,
	reducers: {
		setLocation: (state, action) => {
			state.data.project.location = action.payload.location;
		},
		setSolarUseCase: (state, action) => {
			state.data.project.useCase = action.payload.id;
		},
		setSolarLuminarCount: (state, action) => {
			state.data.project.luminarCount = action.payload.id;
		},
		setSolarShadowLevel: (state, action) => {
			state.data.project.shadowLevel = action.payload.id;
		},
		setSolarAzimuth: (state, action) => {
			state.data.project.orientation = null; // reset orientation data
			state.data.project.azimuth = action.payload.id;
		},
		setSolarActivityLevel: (state, action) => {
			state.data.project.activityLevel = action.payload.id;
		},
		setSolarBatteryType: (state, action) => {
			state.data.project.batteryType = action.payload.id;
		},
		setSolarVersion: (state, action) => {
			state.data.project.version = action.payload.id;
		},
		setSolarTemperature: (state, action) => {
			state.data.project.temperature = action.payload.id;
		},
		setSolarDimmProfile: (state, action) => {
			state.data.project.dimmProfile = action.payload.id;
			if (['profile3', 'profile4', 'profile5'].includes(state.data.project.dimmProfile)) {
				state.data.project.activityLevel = 'none';
			}
		},
		setSolarLumPosition1: (state, action) => {
			state.data.project.lumPosition1 = action.payload.id;
		},
		setSolarLumPosition2: (state, action) => {
			state.data.project.lumPosition2 = action.payload.id;
		},
		setLoadedData: (state, action) => {
			state.data.project.useCase = action.payload.useCase;
			state.data.project.shadowLevel = action.payload.shadowLevel;
			state.data.project.activityLevel = action.payload.activityLevel;
			state.data.project.location = action.payload.location;
			state.data.project.luminarCount = action.payload.luminarCount;
			state.data.project.batteryType = action.payload.batteryType;
			state.data.project.version = action.payload.version;
			state.data.project.temperature = action.payload.temperature;
			state.data.project.dimmProfile = action.payload.dimmProfile;
		},
		setProjectName: (state, action) => {
			state.data.project.projectName = action.payload.text;
		},
		setSolarSelfSufficiency: (state, action) => {
			state.data.project.selfSufficiency = action.payload.selfSufficiency;
		},
		setSolarParameters: (state, action) => {
			state.data.project.parameters = action.payload.parameters;
		},
		setSolarData: (state, action) => {
			state.data.project = action.payload.project;
		},
		setSolarCalculated: (state, action) => {
			state.data.project.calculated = action.payload.solarCalculated;
		},

	},
	extraReducers: (builder) => {
		builder.addCase(fetchPvgisHourly.fulfilled, (state, action) => {
			const orientation = action.meta.arg.orientation;

			// init orientation data if not already available
			if (!state.data.project.orientation) {
				state.data.project.orientation = {
					front: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
					left: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
					right: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
					back: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
				};
			}
			state.data.project.orientation[orientation].pvgisDaily = resampleDaily(action.payload.outputs.hourly);
			state.data.project.orientation[orientation].pvgisMonthly = resampleMonthly(action.payload.outputs.hourly);

			// do pv calculations
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchPvgisHourly.pending, (state, action) => {
			state.data.pvgisDaily = null;
			// state.data.pv... = null;
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchPvgisHourly.rejected, (state, action) => {
			state.data.pvgisDaily = null;
			// state.data.pv... = null;
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchSolarLocation.fulfilled, (state, action) => {
			if (action.payload.results.length > 0) {
				const lat = action.payload.results[0].geometry.location.lat;
				state.data.project.location = {};
				state.data.project.orientation = {
					front: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
					left: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
					right: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
					back: {
						pvgisDaily: null,
						pvgisMonthly: null,
					},
				};
				const addressComponents = action.payload.results[0].address_components.filter(ac => ac.types.includes('sublocality') || ac.types.includes('locality') || ac.types.includes('country'));
				state.data.project.location.name = addressComponents[0].long_name;
				state.data.project.location.latitude = Math.round(action.payload.results[0].geometry.location.lat * 1000) / 1000;
				state.data.project.location.longitude = Math.round(action.payload.results[0].geometry.location.lng * 1000) / 1000;
				const timezone = getFuzzyLocalTimeFromPoint(Date.now(), [state.data.project.location.longitude, state.data.project.location.latitude]);
				const myOffset = new Date().getTimezoneOffset();
				state.data.project.location.timezone = getPosixStringForCurrentYear(timezone._z.name);
				state.data.project.location.timezoneName = timezone._z.name;
				state.data.project.location.offset = timezone._offset + myOffset;
				state.data.locationOutOfRange = false;
				state.data.locationState = LocationState.valid;
			} else {
				state.data.locationState = LocationState.notFound;
				state.data.project.location = null;
			}
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchSolarLocation.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchSolarLocation.rejected, (state, action) => {
			state.data.project.location = null;
			state.status = 'idle';
			state.error = action.payload;
		});

		builder.addCase(fetchSolarGeolocation.fulfilled, (state, action) => {
			if (action.payload.results.length > 0) {
				state.data.project.location = {};
				const addressComponents = action.payload.results[0].address_components.filter(ac => ac.types.includes('sublocality') || ac.types.includes('locality') || ac.types.includes('country'));
				state.data.project.location.name = addressComponents[0].long_name;
				state.data.project.location.latitude = Math.round(action.payload.results[0].geometry.location.lat * 1000) / 1000;
				state.data.project.location.longitude = Math.round(action.payload.results[0].geometry.location.lng * 1000) / 1000;
				const timezone = getFuzzyLocalTimeFromPoint(Date.now(), [state.data.project.location.longitude, state.data.project.location.latitude]);
				const myOffset = new Date().getTimezoneOffset();
				state.data.project.location.timezone = getPosixStringForCurrentYear(timezone._z.name);
				state.data.project.location.timezoneName = timezone._z.name;
				state.data.project.location.offset = timezone._offset + myOffset;
				state.data.locationOutOfRange = false;
				state.data.locationState = LocationState.valid;
			} else {
				state.data.locationState = LocationState.notFound;
				state.data.project.location = null;
			}
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchSolarGeolocation.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchSolarGeolocation.rejected, (state, action) => {
			state.data.project.location = null;
			state.status = 'idle';
			state.error = action.payload;
		});
	}
});

function aggregateItem(item, date, current) {
	if (!item[date])
		item[date] = {
			time: date,
			P: current.P || 0,
			poa_diffuse: current['Gd(i)'] + current['Gr(i)'],
			poa_global: current['Gb(i)'] + current['Gd(i)'] + current['Gr(i)'],
			Int: current.Int || 0
		};
	else {
		item[date].P += current.P || 0;
		item[date].poa_diffuse += (current['Gd(i)'] + current['Gr(i)']) || 0;
		item[date].poa_global += (current['Gb(i)'] + current['Gd(i)'] + current['Gr(i)']) || 0;
		item[date].Int += current.Int || 0;
	}
}

function resampleDaily(hourly) {
	return Object.values(hourly.reduce((item, current) => {
		const date = current.time.substring(0, 8); // days
		aggregateItem(item, date, current);
		return item;
	}, {}));
}

function resampleMonthly(hourly) {
	return Object.values(hourly.reduce((item, current) => {
		const date = current.time.substring(0, 6); // month
		aggregateItem(item, date, current);
		return item;
	}, {}));
}

export const {
	setSolarUseCase,
	setSolarShadowLevel,
	setSolarActivityLevel,
	setSolarLocation,
	setSolarBatteryType,
	setSolarBatterySize,
	setSolarVersion,
	setSolarTemperature,
	setSolarDimmProfile,
	setSolarData,
	setSolarAzimuth,
	setSolarLumPosition1,
	setSolarLumPosition2,
	setSolarSelfSufficiency,
	setSolarParameters,
	setSolarCalculated
} = solarSlice.actions;


export default solarSlice.reducer;
